import React, { useEffect, useRef, useState } from "react";
import ProfileDropdown from "./ProfileDropdown";
import Report20NavGroup from "./Report20NavGroup";
import ClientAdministrationNavGroup from "./ClientAdministrationNavGroup";
import TenantLogoControl from "./TenantLogoControl";
import logoFull from "../../assets/logo-full.png";
import { canAccessClientAdministration, canMutateTenantData } from "../../config/constants";
import { isReport20NavPage } from "../../config/report20Nav";
import { apiService } from "../../services/apiService";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const RESULT_SECTIONS = [
  { key: "circuits", label: "Circuits", type: "circuit" },
  { key: "addresses", label: "Addresses", type: "address" },
  { key: "billingAccounts", label: "Billing accounts", type: "billing_account" },
  { key: "invoices", label: "Invoices", type: "invoice" },
];

function clientInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function flattenResults(data) {
  if (!data) return [];
  const items = [];
  for (const section of RESULT_SECTIONS) {
    const rows = Array.isArray(data[section.key]) ? data[section.key] : [];
    for (const row of rows) {
      items.push({ ...row, type: row.type || section.type, sectionLabel: section.label });
    }
  }
  return items;
}

const Header = ({
  tenant,
  user,
  userRole,
  onNavigate,
  onSearchSelect,
  currentPage,
  primaryTextColor,
  onLogout,
  isSuperAdmin = false,
  showOptionsBar = false,
  optionsContent = null,
  onLogoUpdated,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef(null);
  const wrapRef = useRef(null);
  const requestIdRef = useRef(0);

  const handleNavClick = (page) => (e) => {
    e.preventDefault();
    onNavigate(page);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) {
        return;
      }
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [dropdownOpen]);

  useEffect(() => {
    if (isSuperAdmin) return undefined;

    const q = debouncedQuery;
    if (q.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setSearchLoading(false);
      setActiveIndex(-1);
      return undefined;
    }

    const reqId = ++requestIdRef.current;
    setSearchLoading(true);
    setDropdownOpen(true);

    apiService
      .globalSearch(q)
      .then((res) => {
        if (reqId !== requestIdRef.current) return;
        if (res?.success && res.data) {
          setResults(res.data);
        } else {
          setResults({
            circuits: [],
            addresses: [],
            billingAccounts: [],
            invoices: [],
          });
        }
        setActiveIndex(-1);
      })
      .catch(() => {
        if (reqId !== requestIdRef.current) return;
        setResults({
          circuits: [],
          addresses: [],
          billingAccounts: [],
          invoices: [],
        });
        setActiveIndex(-1);
      })
      .finally(() => {
        if (reqId === requestIdRef.current) setSearchLoading(false);
      });

    return () => {
      requestIdRef.current += 1;
    };
  }, [debouncedQuery, isSuperAdmin]);

  const flatResults = flattenResults(results);
  const showEmpty =
    dropdownOpen &&
    !searchLoading &&
    debouncedQuery.length >= MIN_QUERY_LENGTH &&
    results &&
    flatResults.length === 0;
  const showDropdown =
    !isSuperAdmin &&
    dropdownOpen &&
    (searchLoading ||
      showEmpty ||
      (results && flatResults.length > 0) ||
      (searchQuery.trim().length > 0 && searchQuery.trim().length < MIN_QUERY_LENGTH));

  const selectResult = (item) => {
    if (!item || typeof onSearchSelect !== "function") return;
    onSearchSelect(item);
    setSearchQuery("");
    setDebouncedQuery("");
    setResults(null);
    setDropdownOpen(false);
    setActiveIndex(-1);
    setMobileMenuOpen(false);
  };

  const onSearchKeyDown = (e) => {
    if (!showDropdown) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setDropdownOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (flatResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? flatResults.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      selectResult(flatResults[activeIndex]);
    }
  };

  const tenantName = tenant?.name || "";
  const hasLogo =
    tenant?.logo &&
    typeof tenant.logo === "string" &&
    tenant.logo.startsWith("data:image/");
  const initials = clientInitials(tenantName);
  const canEditLogo = !isSuperAdmin && canMutateTenantData(userRole);

  let flatIndex = -1;

  return (
    <header className={`page-topbar ${mobileMenuOpen ? "show-menu" : ""}`}>
      <div className="navbar-header overflow-visible">
        <div className="link-items">
          <a
            href="#"
            className="jebl-logo-tile"
            onClick={handleNavClick(isSuperAdmin ? "dashboard" : "overview")}
            title="JEBL Solutions"
          >
            <img src={logoFull} alt="JEBL Solutions" />
          </a>

          {isSuperAdmin ? (
            <div className="client-chip" title="Super Admin">
              <span className="client-chip-mark">
                <span className="client-chip-initials" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 3.5h10l4.5 7.5L17 18.5H7L2.5 11z" />
                  </svg>
                </span>
              </span>
              <span className="client-chip-name">Super Admin</span>
            </div>
          ) : (
            <div className="client-chip" title={tenantName}>
              <span className="client-chip-mark">
                {hasLogo ? (
                  <img src={tenant.logo} alt={`${tenantName} logo`} />
                ) : (
                  <span className="client-chip-initials">{initials || "—"}</span>
                )}
              </span>
              {tenantName ? <span className="client-chip-name">{tenantName}</span> : null}
              {canEditLogo ? (
                <TenantLogoControl
                  tenant={tenant}
                  userRole={userRole}
                  isSuperAdmin={isSuperAdmin}
                  onLogoUpdated={onLogoUpdated}
                />
              ) : null}
            </div>
          )}
        </div>

        <div className="rt-links">
          <div className="nav-links" role="navigation" aria-label="Main navigation">
            {isSuperAdmin && (
              <>
                <a
                  href="#"
                  onClick={handleNavClick("dashboard")}
                  className={
                    currentPage === "dashboard" || currentPage === "" ? "active" : ""
                  }
                >
                  Tenants
                </a>
                <a
                  href="#"
                  onClick={handleNavClick("ai-prompts")}
                  className={currentPage === "ai-prompts" ? "active" : ""}
                >
                  AI prompts
                </a>
                <a
                  href="#"
                  onClick={handleNavClick("login-audit")}
                  className={currentPage === "login-audit" ? "active" : ""}
                >
                  Login audit log
                </a>
              </>
            )}
            {!isSuperAdmin && (
              <>
                <a
                  href="#"
                  onClick={handleNavClick("overview")}
                  className={currentPage === "overview" ? "active" : ""}
                >
                  Overview
                </a>
                <a
                  href="#"
                  onClick={handleNavClick("inventory")}
                  className={currentPage === "inventory" ? "active" : ""}
                >
                  Detailed Inventory
                </a>
                {canAccessClientAdministration(userRole) && (
                  <ClientAdministrationNavGroup
                    currentPage={currentPage}
                    onNavigate={(page) => {
                      onNavigate(page);
                      setMobileMenuOpen(false);
                    }}
                  />
                )}
                {!canAccessClientAdministration(userRole) && (
                  <a
                    href="#"
                    onClick={handleNavClick("master-address")}
                    className={currentPage === "master-address" ? "active" : ""}
                  >
                    Master Address
                  </a>
                )}
                <Report20NavGroup
                  currentPage={currentPage}
                  onNavigate={(page) => {
                    onNavigate(page);
                    setMobileMenuOpen(false);
                  }}
                />
                <a
                  href="#"
                  onClick={handleNavClick("invoices")}
                  className={currentPage === "invoices" ? "active" : ""}
                >
                  Invoices
                </a>
              </>
            )}
          </div>

          {!isSuperAdmin && (
            <div className="global-search-wrap" ref={wrapRef}>
              <svg
                className="global-search-icon"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                ref={searchRef}
                type="search"
                className="global-search-input"
                placeholder="Find circuit, account or address"
                aria-label="Find circuit, account, address or invoice"
                aria-autocomplete="list"
                aria-expanded={showDropdown}
                aria-controls="global-search-results"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                onKeyDown={onSearchKeyDown}
                autoComplete="off"
              />
              {showDropdown && (
                <div
                  id="global-search-results"
                  className="global-search-dropdown"
                  role="listbox"
                >
                  {searchQuery.trim().length > 0 &&
                    searchQuery.trim().length < MIN_QUERY_LENGTH && (
                      <div className="global-search-hint">
                        Type at least {MIN_QUERY_LENGTH} characters
                      </div>
                    )}
                  {searchLoading && (
                    <div className="global-search-hint">Searching…</div>
                  )}
                  {showEmpty && (
                    <div className="global-search-empty">No results found.</div>
                  )}
                  {!searchLoading &&
                    RESULT_SECTIONS.map((section) => {
                      const rows = Array.isArray(results?.[section.key])
                        ? results[section.key]
                        : [];
                      if (rows.length === 0) return null;
                      return (
                        <div key={section.key} className="global-search-section">
                          <div className="global-search-section-label">
                            {section.label}
                          </div>
                          {rows.map((item) => {
                            flatIndex += 1;
                            const index = flatIndex;
                            const active = index === activeIndex;
                            return (
                              <button
                                key={`${section.key}-${item.id}-${index}`}
                                type="button"
                                role="option"
                                aria-selected={active}
                                className={`global-search-item${active ? " active" : ""}`}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => selectResult(item)}
                              >
                                <span className="global-search-item-label">
                                  {item.label}
                                </span>
                                {item.subtitle ? (
                                  <span className="global-search-item-sub">
                                    {item.subtitle}
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {!isSuperAdmin && <span className="header-hairline" aria-hidden="true" />}

          {user && (
            <ProfileDropdown
              user={user}
              userRole={userRole}
              isSuperAdmin={isSuperAdmin}
              onLogout={onLogout}
            />
          )}
        </div>

        <button
          type="button"
          className="open-menu"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Open menu"
          aria-expanded={mobileMenuOpen}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {showOptionsBar && optionsContent && (
        <div
          className={`max-w-full lg:max-w-[85%] mx-5 lg:mx-auto mt-0.5 overflow-x-auto whitespace-nowrap ${
            currentPage === "analytics" || isReport20NavPage(currentPage)
              ? "bg-white rounded-md shadow-md px-4 py-3 header-options-bar-analytics"
              : "bg-white rounded-md shadow-md px-4 py-3 mb-[-30px] flex items-center"
          }`}
        >
          {optionsContent}
        </div>
      )}
    </header>
  );
};

export default Header;
